import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Home,
  Users,
  FileText,
  CreditCard,
  X,
  Calendar,
  Menu,
  Shield,
  Gavel,
  ChevronDown,
  Brain,
  Bot,
  Mail,
  Settings,
  Receipt,
  Lock,
  BookOpen,
} from "lucide-react";
import { cn } from "@core/lib/utils";
import {
  getProductConfig,
  getThemeClasses,
  type ProductType,
} from "@core/config/products.config";
import { useAuth } from "@core/contexts/AuthContext";
import { AuthModal } from "@core/components/modals/AuthModal";

interface ProductSidebarProps {
  productType: ProductType;
  onClose?: () => void;
  className?: string;
  isOpen?: boolean;
  onToggle?: () => void;
  hideHamburger?: boolean;
}

export const ProductSidebar: React.FC<ProductSidebarProps> = ({
  productType,
  onClose,
  className,
  isOpen: propIsOpen,
  onToggle: propOnToggle,
  hideHamburger = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  // Source de vérité pour l'abonnement : profile.plan_type (cf. useFreemiumQuota)
  const hasPaidPlan =
    !!profile?.plan_type &&
    profile.plan_type.toLowerCase() !== "free" &&
    profile.plan_type.toLowerCase() !== "guest";
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const openSignInModal = () => {
    if (onClose) onClose();
    if (propOnToggle && isOpen) propOnToggle();
    else if (!propOnToggle) setInternalIsOpen(false);
    setAuthModalOpen(true);
  };

  // Utiliser les props si fournies, sinon l'état interne
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = propIsOpen !== undefined ? propIsOpen : internalIsOpen;
  const setIsOpen = propOnToggle || setInternalIsOpen;
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const config = getProductConfig(productType);
  const theme = getThemeClasses(config.theme);
  const Icon = config.icon;

  const handleLinkClick = () => {
    console.log("🔍 [ProductSidebar] handleLinkClick appelé");
    // 1. Dispatch reset event pour réinitialiser le chat ET le formulaire
    const resetEvent = new CustomEvent("chat-reset", {
      detail: { resetType: "full" },
    });
    window.dispatchEvent(resetEvent);

    // 2. Fermer la sidebar
    if (onClose) onClose();
    // Toujours fermer la sidebar lors d'un clic sur un lien
    if (propOnToggle && isOpen) {
      // Si la sidebar est contrôlée par des props et qu'elle est ouverte, la fermer
      propOnToggle();
    } else if (!propOnToggle) {
      // Si la sidebar utilise l'état interne, la fermer directement
      setInternalIsOpen(false);
    }
    // Fermer toutes les sections déroulantes pour le comportement mobile
    setOpenSections({});
  };

  const toggleSidebar = () => {
    if (propOnToggle) {
      propOnToggle();
    } else {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      // Si la section est déjà ouverte, la fermer
      if (prev[section]) {
        return { ...prev, [section]: false };
      }
      // Sinon, fermer toutes les autres sections et ouvrir celle-ci
      const newSections: Record<string, boolean> = {};
      newSections[section] = true;
      return newSections;
    });
  };

  const CollapsibleSection = ({
    title,
    sectionKey,
    icon: Icon,
    children,
  }: {
    title: string;
    sectionKey: string;
    icon: any;
    children: React.ReactNode;
  }) => {
    const isOpen = openSections[sectionKey] || false;
    const panelId = `sidebar-section-${sectionKey}`;

    return (
      <div>
        <button
          onClick={() => toggleSection(sectionKey)}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors w-full text-left"
        >
          <Icon className="w-5 h-5" />
          <span className="flex-1">{title}</span>
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform duration-200",
              isOpen && "rotate-180",
            )}
          />
        </button>
        {isOpen && (
          <div
            id={panelId}
            className="ml-8 mt-1 space-y-1 animate-in slide-in-from-top-1 duration-200"
          >
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Hamburger géré par la sidebar elle-même */}
      {!hideHamburger && (
        <button
          onClick={toggleSidebar}
          style={{
            left: isOpen ? 272 : 16,
          }}
          className="fixed top-4 z-[100] p-3 md:p-2 bg-[#1a1a1a] text-white rounded-lg shadow-md hover:bg-gray-800 transition-all duration-300"
          aria-label={isOpen ? t("sidebar_close_menu") : t("sidebar_open_menu")}
          data-testid="productsidebar-menu-toggle"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Sidebar - Toujours monté mais masqué par CSS pour la robustesse et les transitions */}
      <div
        className={cn(
          "fixed left-0 top-0 w-64 bg-[#1a1a1a] border-r border-gray-800 h-screen-safe flex flex-col z-[90] transition-all duration-300 ease-in-out",
          isOpen
            ? "translate-x-0 opacity-100 visible"
            : "-translate-x-full opacity-0 invisible",
          className,
        )}
        data-testid="product-sidebar"
      >
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          {/* Logo contextuel : DooDates + Produit */}
          <Link
            to="/?home=1"
            className="flex items-center gap-2 group"
            onClick={handleLinkClick}
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="font-bold text-white text-sm">D</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-200 to-purple-600">
                  DooDates
                </span>
                <span
                  className={cn(
                    "text-xs font-medium uppercase tracking-wider text-gray-300",
                  )}
                >
                  {t(`product_name_${config.type}`, {
                    defaultValue: config.name,
                  })}
                </span>
              </div>
            </div>
          </Link>
        </div>

        <div className="p-4">
          <button
            data-testid={`create-${productType}-poll`}
            onClick={(e) => {
              // 1. Dispatch reset event
              const resetEvent = new CustomEvent("chat-reset", {
                detail: { resetType: "full" },
              });
              window.dispatchEvent(resetEvent);

              // 2. Navigate with ?new= timestamp to force chat remount (even if already on /create)
              navigate(`${config.createRoute}?new=${Date.now()}`);

              // 3. Close sidebar (using existing logic)
              handleLinkClick();
            }}
            className={cn(
              "flex items-center justify-center gap-2 w-full py-3 px-4 text-white rounded-lg font-medium transition-colors shadow-sm cursor-pointer border-none",
              theme.button,
            )}
          >
            <Plus className="w-5 h-5" />
            {t(`create_label_${config.type}`, {
              defaultValue: config.createLabel,
            })}
          </button>
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          <Link
            to="/?home=1"
            onClick={handleLinkClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              location.pathname === "/"
                ? cn(theme.bg, theme.text)
                : "text-gray-400 hover:bg-gray-800 hover:text-white",
            )}
            data-testid="home-link"
          >
            <Home className="w-5 h-5" />
            {t("sidebar_home")}
          </Link>

          {user ? (
            <Link
              to={config.dashboardRoute}
              onClick={handleLinkClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive(config.dashboardRoute)
                  ? cn(theme.bg, theme.text)
                  : "text-gray-400 hover:bg-gray-800 hover:text-white",
              )}
              data-testid="dashboard-link"
            >
              <Home className="w-5 h-5" />
              {t("sidebar_dashboard")}
            </Link>
          ) : (
            <button
              type="button"
              onClick={openSignInModal}
              title={t("sidebar_dashboard_locked_tooltip")}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-800 hover:text-white transition-colors w-full text-left"
              data-testid="dashboard-link"
            >
              <Home className="w-5 h-5" />
              <span className="flex-1">{t("sidebar_dashboard")}</span>
              <Lock className="w-4 h-4 opacity-70" />
            </button>
          )}

          {/* Liens dynamiques configurés par produit (ex: Historique Quizz) */}
          {config.extraLinks?.map((link) => (
            <Link
              key={link.route}
              to={link.route}
              onClick={handleLinkClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive(link.route)
                  ? cn(theme.bg, theme.text)
                  : "text-gray-400 hover:bg-gray-800 hover:text-white",
              )}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}

          {/* Anciens liens "Ressources" promus dans le menu principal */}
          {user ? (
            <Link
              to="/data-control"
              onClick={handleLinkClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive("/data-control")
                  ? cn(theme.bg, theme.text)
                  : "text-gray-400 hover:bg-gray-800 hover:text-white",
              )}
            >
              <Shield className="w-5 h-5" />
              {t("sidebar_my_data")}
            </Link>
          ) : (
            <button
              type="button"
              onClick={openSignInModal}
              title={t("sidebar_my_data_locked_tooltip")}
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-800 hover:text-white transition-colors w-full text-left"
            >
              <Shield className="w-5 h-5" />
              <span className="flex-1">{t("sidebar_my_data")}</span>
              <Lock className="w-4 h-4 opacity-70" />
            </button>
          )}
          <Link
            to={config.docsRoute}
            onClick={handleLinkClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              isActive(config.docsRoute)
                ? cn(theme.bg, theme.text)
                : "text-gray-400 hover:bg-gray-800 hover:text-white",
            )}
          >
            <FileText className="w-5 h-5" />
            {t("sidebar_documentation")}
          </Link>
          <Link
            to="/guides"
            onClick={handleLinkClick}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              isActive("/guides")
                ? cn(theme.bg, theme.text)
                : "text-gray-400 hover:bg-gray-800 hover:text-white",
            )}
          >
            <BookOpen className="w-5 h-5" />
            {t("sidebar_guides")}
          </Link>
          {!hasPaidPlan && (
            <Link
              to={config.pricingRoute}
              onClick={handleLinkClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                isActive(config.pricingRoute)
                  ? cn(theme.bg, theme.text)
                  : "text-gray-400 hover:bg-gray-800 hover:text-white",
              )}
            >
              <CreditCard className="w-5 h-5" />
              {t("sidebar_pricing")}
            </Link>
          )}
          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(new CustomEvent("support-widget:open"));
              if (onClose) onClose();
              if (propOnToggle && isOpen) propOnToggle();
              else if (!propOnToggle) setInternalIsOpen(false);
            }}
            title={t("sidebar_ai_assistant_tooltip")}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left",
              "text-gray-400 hover:bg-gray-800 hover:text-white",
            )}
          >
            <Bot className="w-5 h-5" />
            {t("sidebar_ai_assistant")}
          </button>
          <Link
            to="/support"
            onClick={handleLinkClick}
            title={t("sidebar_support_tooltip")}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              isActive("/support")
                ? cn(theme.bg, theme.text)
                : "text-gray-400 hover:bg-gray-800 hover:text-white",
            )}
          >
            <Mail className="w-5 h-5" />
            {t("sidebar_contact_support")}
          </Link>

          {/* Paramètres de compte (users connectés uniquement) */}
          {user && (
            <div className="pt-2 mt-2 border-t border-gray-800">
              <CollapsibleSection
                title={t("sidebar_settings")}
                sectionKey="settings"
                icon={Settings}
              >
                <Link
                  to="/account/subscription"
                  onClick={handleLinkClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive("/account/subscription")
                      ? cn(theme.bg, theme.text)
                      : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  )}
                  data-testid="sidebar-subscription-link"
                >
                  <CreditCard className="w-4 h-4" />
                  {t("sidebar_my_subscription")}
                </Link>
                <Link
                  to="/account/invoices"
                  onClick={handleLinkClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive("/account/invoices")
                      ? cn(theme.bg, theme.text)
                      : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  )}
                >
                  <Receipt className="w-4 h-4" />
                  {t("sidebar_invoices")}
                </Link>
                <Link
                  to="/account/security"
                  onClick={handleLinkClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive("/account/security")
                      ? cn(theme.bg, theme.text)
                      : "text-gray-400 hover:bg-gray-800 hover:text-white",
                  )}
                >
                  <Lock className="w-4 h-4" />
                  {t("sidebar_security")}
                </Link>
              </CollapsibleSection>
            </div>
          )}

          {/* Légal & Confidentialité (positionné juste sous la liste, pas collé au bas) */}
          <div className="pt-2 mt-2 border-t border-gray-800">
            <CollapsibleSection
              title={t("sidebar_legal_privacy")}
              sectionKey="legal"
              icon={Shield}
            >
              <Link
                to="/terms"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <FileText className="w-4 h-4" />
                {t("legal:terms_title")}
              </Link>
              <Link
                to="/privacy"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Shield className="w-4 h-4" />
                {t("legal:privacy_title")}
              </Link>
              <Link
                to="/legal"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Gavel className="w-4 h-4" />
                {t("legal:legal_notice_title")}
              </Link>
              <Link
                to="/docs/ia-policy"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
              >
                <Brain className="w-4 h-4" />
                {t("legal:ai_policy_title")}
              </Link>
            </CollapsibleSection>
          </div>
        </nav>
      </div>
      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultMode="signin"
      />
    </>
  );
};
